import CloudDoneIcon from '@mui/icons-material/CloudDone'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ConfirmDialog } from '../../components/common/ConfirmDialog'
import { ErrorAlert, LoadingBlock } from '../../components/common/Feedback'
import { PageHeader } from '../../components/common/PageHeader'
import { api } from '../../lib/api'
import { formatDateTime } from '../../lib/format'

type DriveStatus = {
  connected: boolean
  driver: 'drive' | 'local'
  scope: 'drive.file' | 'drive'
  rootFolderName: string
  email: string | null
  rootFolderId: string | null
  connectedAt: string | null
  connectedByName: string | null
}

const CALLBACK_ERRORS: Record<string, string> = {
  INVALID_STATE: 'תוקף הבקשה פג. נסה לחבר מחדש.',
  NO_CODE: 'החיבור בוטל.',
  access_denied: 'החיבור בוטל — לא אושרה ההרשאה.',
  DRIVE_CONNECT_FAILED: 'החיבור נכשל. בדוק שה-redirect URI רשום ב-OAuth client.',
  NO_REFRESH_TOKEN:
    'Google לא החזיר refresh token. יש לבטל את ההרשאה בחשבון (myaccount.google.com → Security → Third-party access) ולנסות שוב.',
}

function StatusRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Stack>
  )
}

export function DriveSettingsPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  const justConnected = searchParams.get('connected') === '1'
  const callbackError = searchParams.get('error')

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['drive-status'],
    queryFn: () => api.get<DriveStatus>('/settings/drive'),
  })

  const disconnect = useMutation({
    mutationFn: () => api.del('/settings/drive'),
    onSuccess: () => {
      setConfirmDisconnect(false)
      void queryClient.invalidateQueries({ queryKey: ['drive-status'] })
    },
  })

  const clearBanner = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('connected')
    next.delete('error')
    setSearchParams(next, { replace: true })
  }

  return (
    <>
      <PageHeader
        title="חיבור Google Drive"
        subtitle="המסמכים נשמרים בחשבון Google אחד של המשרד. העובדים ניגשים אליהם דרך המערכת."
      />

      {justConnected ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearBanner}>
          Drive חובר בהצלחה.
        </Alert>
      ) : null}

      {callbackError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearBanner}>
          {CALLBACK_ERRORS[callbackError] ?? 'החיבור נכשל.'}
        </Alert>
      ) : null}

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {disconnect.error ? <ErrorAlert error={disconnect.error} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {data ? (
        <>
          {data.driver === 'local' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>אחסון מקומי פעיל</AlertTitle>
              השרת מוגדר כרגע ל-<code>STORAGE_DRIVER=local</code> — המסמכים נשמרים בדיסק ולא
              ב-Drive. זה מיועד לפיתוח בלבד; בפרודקשן השרת מסרב לעלות במצב הזה.
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              {data.connected ? (
                <CloudDoneIcon color="success" />
              ) : (
                <CloudOffIcon color="disabled" />
              )}
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {data.connected ? 'מחובר' : 'לא מחובר'}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={data.scope === 'drive.file' ? 'גישה לקבצי המערכת בלבד' : 'גישה מלאה ל-Drive'}
              />
            </Stack>

            <Divider />

            {data.connected ? (
              <Box sx={{ mt: 1 }}>
                <StatusRow label="חשבון" value={data.email} />
                <StatusRow
                  label="תיקיית שורש"
                  value={
                    data.rootFolderId ? (
                      <Link
                        href={`https://drive.google.com/drive/folders/${data.rootFolderId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {data.rootFolderName} <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      `תיווצר בהעלאת המסמך הראשון בשם "${data.rootFolderName}"`
                    )
                  }
                />
                <StatusRow label="חובר בתאריך" value={formatDateTime(data.connectedAt)} />
                <StatusRow label="חובר על ידי" value={data.connectedByName ?? '—'} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                עד שיחובר חשבון, העלאת מסמכים תיכשל.
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
              <Button variant="contained" href="/api/settings/drive/connect">
                {data.connected ? 'חיבור חשבון אחר' : 'חיבור חשבון Google'}
              </Button>
              {data.connected ? (
                <Button color="error" onClick={() => setConfirmDisconnect(true)}>
                  ניתוק
                </Button>
              ) : null}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              מה חשוב לדעת
            </Typography>
            <Stack spacing={1.25}>
              <Typography variant="body2">
                <strong>העובדים לא צריכים גישה ל-Drive.</strong> הם רואים ומורידים מסמכים דרך
                המערכת, וההרשאה נשלטת על ידי ההתחברות אליה.
              </Typography>
              <Typography variant="body2">
                <strong>אם בכל זאת תשתף איתם את התיקייה</strong> — שתף אותה ישירות מ-Drive. זו
                הרשאה אנושית ואינה תלויה בהרשאות המערכת.
              </Typography>
              <Typography variant="body2">
                <strong>אין להזיז או למחוק קבצים ידנית מ-Drive.</strong> מחיקה מנתקת את הקישור,
                והמערכת תציג ״הקובץ לא נמצא באחסון״ במקום המסמך. הרשומה עצמה תישמר.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.scope === 'drive.file'
                  ? 'המערכת רואה רק קבצים שהיא עצמה יצרה — היא לא ניגשת לשאר ה-Drive של החשבון.'
                  : 'המערכת מוגדרת עם גישה מלאה ל-Drive של החשבון.'}
              </Typography>
            </Stack>
          </Paper>
        </>
      ) : null}

      <ConfirmDialog
        open={confirmDisconnect}
        title="ניתוק Google Drive"
        message="המערכת תאבד גישה למסמכים עד שיחובר חשבון מחדש. הקבצים עצמם יישארו ב-Drive."
        confirmLabel="ניתוק"
        busy={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
        onClose={() => setConfirmDisconnect(false)}
      />
    </>
  )
}
